import { telemetryInternalIds } from './telemetry-identifiers';
import {
  logFailureReason,
  FaActionName,
  FaActionSequence,
  StructuredLogger,
} from './structured-logger.service';

/** One instance per workflow owner. Keys are local only; never emitted. */
export class FaActionTracker {
  private readonly sequences = new Map<
    string,
    { sequence: FaActionSequence; created: number }
  >();

  constructor(private readonly logger: StructuredLogger) {}

  async run<T>(
    name: FaActionName,
    resourceKey: string,
    work: (attempt: FaTrackedAttempt) => Promise<T>,
    observe = true,
  ): Promise<T> {
    if (!observe) return work(new FaTrackedAttempt());
    const key = `${name}:${resourceKey}`;
    let tracked = this.sequences.get(key);
    if (!tracked || Date.now() - tracked.created > 10 * 60_000) {
      tracked = { sequence: this.logger.action(name), created: Date.now() };
      this.sequences.set(key, tracked);
      const oldest = this.sequences.keys().next().value;
      if (this.sequences.size > 128 && oldest !== undefined)
        this.sequences.delete(oldest);
    }
    const terminal = tracked.sequence.begin();
    const attempt = new FaTrackedAttempt();
    try {
      const result = await work(attempt);
      terminal.succeed(attempt.stage, attempt.accepted, attempt.identifiers);
      if (this.sequences.get(key) === tracked) this.sequences.delete(key);
      return result;
    } catch (error) {
      if (attempt.completedCount >= attempt.operationCount) {
        terminal.succeed('save', false, attempt.identifiers);
        this.sequences.delete(key);
        if (attempt.stage === 'refresh')
          this.logger.emit('feature.operation.failed', {
            ...telemetryInternalIds(attempt.identifiers),
            feature: name.split('.')[0],
            stage: 'refresh',
            trigger: 'user',
            outcome: 'failed',
            ...logFailureReason(error),
          });
        throw error;
      }
      const failure = attempt.failure ?? error;
      terminal.fail(
        failure,
        attempt.stage,
        attempt.dispatched,
        attempt.localRejection &&
          !attempt.dispatched &&
          logFailureReason(failure).http_status === undefined
          ? 'stale_state'
          : undefined,
        {
          ...telemetryInternalIds(attempt.identifiers),
          completed_count: attempt.completedCount,
          operation_count: attempt.operationCount,
        },
        attempt.uncertain,
      );
      throw error;
    }
  }
}

export class FaTrackedAttempt {
  identifiers: Record<string, unknown> = {};
  failure: unknown;
  uncertain = false;
  localRejection = true;
  stage = 'submission';
  dispatched = false;
  accepted = false;
  completedCount = 0;
  operationCount = 1;

  request<T>(request: T): T {
    this.dispatched = true;
    return request;
  }
}
